// Read-only GL Summary rollup (planning.md §4). Sits directly below the
// Line Items Summary on every screen that shows line items: one row per distinct
// code chosen, with that code's summed line total, sorted descending by
// total, plus a grand-total row (which equals the line items total by
// construction — the two blocks read against each other at a glance).
//
// NO COMPANY COLUMN (code review D-04/D-06, 2026-10-03). An invoice has one
// company (owner item 5, 2026-08-17), so there is nothing to break down by
// company. A per-company breakdown stood here for cross-company splits, and it
// showed on every Import Control invoice opened read-only, because it compared
// the lines with the partition, which is empty before release.
//
// Unassigned lines group under a single "(No code)" row so the totals still
// add up and the gap stays visible. A HALF-CODED line — one that has an
// account and is waiting on a department — groups there too, but the fact that
// it is half-coded is NOT written into the table as a row: on 2026-08-11 the
// owner asked that the waiting state not appear as a row of this table. It is
// stated once beneath the grand total instead, in the same `DotPill` shape as
// the Line Items Summary's "Reconciles" chip.
//
// That line carries the half-coded DOLLARS, not just a count. A count cannot say
// whether the gap is $40 of paper towels or half the invoice, and the amount is
// the part a reviewer acts on — so dropping the labelled row must not drop the
// figure with it. The pill states both.
//
// THE EDITABLE FORM'S OWN MOUNT GROUPS BY ACCOUNT, not by code
// (`groupBy="account"`) — at the one step `EditableInvoiceForm` covers, Import
// Control, a line's department is often not chosen yet while the reviewer
// works (it is required only at release, D-309), so grouping by code would
// scatter the rollup. It was
// AP/AR Approval's mount until that step merged into Import Control (D-215).
//
// Derived display only — never editable, never written back. One shared
// component so the surfaces can't drift; callers pass a flat row list and this
// groups/sums.
//
// Presented as a peer section of the Line Items Summary it follows — same heading
// component, same divider, same table-header styling. It used to render its own
// micro-caps heading and column labels, which read as a different design
// language from every block around it (user note 2026-08-02).
//
// THE GRAND TOTAL ROW carries a green "Reconciles" `DotPill` INLINE with the
// Total amount (owner, 2026-08-14, reverting item 15/2026-08-13's move of the
// pill to its own right-aligned row below the table — the Line Items Summary's
// own `ReconcileFooter` in `InvoiceLineItemsTable.tsx` never left that pattern,
// and the owner asked for the two to read the same way) when `subtotal`/`tax`/
// `total` are supplied and pass the same `checkAmounts` test as that footer
// (owner item 16, 2026-08-12) — never a red one; a mismatch just shows no pill.
// See `totalCell`'s comment below for how "inline" is done without breaking
// the amount COLUMN's own right-edge alignment with the code rows above it.
//
// The "View With Tax & Shipping Distributed" toggle (owner, 2026-08-14) sits in that same
// inline group: OFF (default) shows each code's raw pre-charge subtotal,
// exactly as this component always has; ON, it runs the invoice's tax AND its
// shipping through the identical proportional-spread rule the export uses —
// two separate allocations, as there.
//
// THE DISTRIBUTED FIGURES ARE THE CSV'S, TO THE CENT (owner, 2026-09-25 —
// `docs/decisions.md` D-151: "The CSV file produced at Export Control should
// match the values decided inside the modal"). The CSV has one row per LINE
// (D-40) and spreads each charge, its remainder cents and the vendor's
// variance of up to three cents across LINES, the largest line taking the
// leftovers. This rollup groups by code (or, at Import Control, by account), so it
// does NOT spread over its own groups — that would put the leftovers on the
// largest GROUP instead. It spreads at line grain first and then adds up by
// group (`distributeByGroup` in `app/src/lib/taxDistribution.ts`), so every
// group shows exactly the sum of its lines' CSV rows. That holds at the
// account grouping too (D-7): an account's figure is the sum of the CSV rows
// whose code carries that account. The allocation is shared `allocate`.
//
// THE FAR-LEFT "Total" LABEL IS GONE (owner, 2026-08-14): the row is the last in
// the table, bold, and already carries a bolded "Invoice Subtotal:" /
// "Invoice Total:" against the figure — a second word for the same thing at the
// other end of the row said nothing the right-hand label does not.
//
// A bolded label sits last in that inline group, right against the amount
// cell (owner, 2026-08-14 — the same treatment, from the same shared
// `useSummaryLabelStyles` class, as the Line Items Summary's own
// `ReconcileFooter`): "Invoice Subtotal:" while the toggle is off (or absent),
// "Invoice Total:" once it is on and actually distributing — naming what the
// grand-total figure beside it actually is, the same way the toggle already
// changes what that figure actually shows.
import {
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
// PORT NOTE: the `compact` heading variant existed for the claim detail modal,
// which titled its sections differently. Claims is gone, so this renders one
// way — as a peer section of the Line Items Summary above it.
import { useState } from 'react'
import { checkAmounts, formatCurrency, toNumber } from '../lib/format'
import { TINT_PILLS, useSummaryLabelStyles } from '../lib/theme'
import { DotPill } from './Badges'
import { distributeByGroup } from '../lib/taxDistribution'

/** One line's contribution: which code and how much. */
interface GLSummaryRow {
  codeId?: string | null
  codeName?: string | null
  /** "Account" (owner item 8, 2026-08-19 — formerly "What is it?") — the
      grouping key at Import Control. */
  accountId?: string | null
  accountLabel?: string | null
  total: number
  /** The line's own sequence (`line_number`) — the tie-break key the "Distribute
      Tax & Shipping" toggle uses to place a rounding remainder exactly where the
      export would (`app/src/lib/taxDistribution.ts`). Optional: a caller that
      omits it still gets a correct SUM, just not a guaranteed-identical tie-break
      on the rare exact-tie case — see that module's `DistributableRow` comment. */
  lineNumber?: number | null
}

interface Props {
  rows: GLSummaryRow[]
  /**
   * `'account'` at Import Control, where the department must not appear.
   * `'code'` everywhere else. Defaults to `'code'`.
   */
  groupBy?: 'code' | 'account'
  /** Invoice amounts, for the green "Reconciles" pill on the Total row — the
      same DotPill on the same `checkAmounts` test as the Line Items Summary
      footer above (`InvoiceLineItemsTable`'s ReconcileFooter, owner item 16,
      2026-08-12). Omitted (or all null) = no pill, never a red one. Also what
      the "View With Tax & Shipping Distributed" toggle spreads: no amounts
      known = nothing to distribute, so the toggle does not render at all.
      Known charges that are both zero no longer disable it (owner item 4,
      2026-08-18) — see the toggle's own comment for why a zero pair was
      never a real reason to lock it. */
  subtotal?: number | string | null
  tax?: number | string | null
  shipping?: number | string | null
  total?: number | string | null
}

// "Code", matching this bucket's OWN column header (`groupBy === 'code'`
// heads the column `Code`, in the JSX below). It said `(No account)`
// until a walk read the two together (2026-08-30): the empty bucket collected
// lines that have an Account and lack a DEPARTMENT, so the row said "no account"
// about a line whose Account was set, two rows above a pill correctly saying
// `awaiting a Department`. It then said `(No GL code)` while the header read
// `GL Code`, and follows the header to `(No code)` with the owner's ruling of
// 2026-09-24 (`docs/decisions.md` D-139). `docs/glossary.md` is explicit:
// "On the invoice screens the whole code appears only in the **GL Summary**
// rollup, whose column is headed **Code** (owner, 2026-09-24), so it never
// shares a word with the **Account** column."
const NO_CODE = '(No code)'
// Every place a user can read the trailing half calls it **Account** (owner
// item 26, 2026-08-28). This rollup was one of the last two places still
// printing the old word then; only the label changed.
// This bucket's column header IS `Account`, and a line in it genuinely has no
// account, so the bare word is correct here.
const NO_ACCOUNT = '(No account)'
/** A line with an account and no department: coded, just not finished.
    Reported beneath the total rather than as a row of its own. */
function isAwaitingDepartment(r: GLSummaryRow): boolean {
  return !r.codeId?.trim() && !r.codeName?.trim() && !!r.accountId?.trim()
}

const useStyles = makeStyles({
  // Peer section on the invoice surfaces: rule, heading, table — the same
  // rhythm (and the same 16px gaps) as the Line Items Summary above it.
  wrap: { display: 'flex', flexDirection: 'column', gap: '16px' },
  table: { tableLayout: 'fixed', width: '100%', fontVariantNumeric: 'tabular-nums' },
  num: { textAlign: 'right' },
  numHead: {
    textAlign: 'right',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end', width: '100%' },
  },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  grandRow: { fontWeight: tokens.fontWeightSemibold },
  // THE TOTAL ROW'S LABEL CELL — "Total" plus, on the SAME line, the
  // "Distribute tax" toggle and the "Reconciles" pill, right-hugging the
  // amount cell beside it (owner, 2026-08-14: inline with the total, the way
  // `InvoiceLineItemsTable`'s `ReconcileFooter` already reads).
  //
  // HOW THE AMOUNT COLUMN'S ALIGNMENT IS PRESERVED: the amount cell itself
  // (`style={{ width: 120 }}` + `s.num`, tabular-nums) is untouched — this
  // class lives on the LABEL cell next door, not the amount cell, so nothing
  // here changes that cell's width, its `text-align`, or the fixed-layout
  // column it shares with every grouped row above it. `justifyContent:
  // 'space-between'` puts "Total" on the left and the toggle+pill group hard
  // against this cell's OWN right edge — which is the amount cell's left
  // edge — so the group reads as sitting immediately left of the amount,
  // without the amount cell ever having to make room for a pill inside it
  // (the thing item 15/2026-08-13 broke alignment trying to do; verified by
  // re-running the layout: every grouped-row amount, the grand total, and
  // this row's amount all still share the same 120px right-aligned column).
  // `flex-end`, not `space-between`: with the far-left "Total" removed (owner,
  // 2026-08-14) there is only one child, and `space-between` would park it on the
  // LEFT edge of the cell — as far from the amount as the row is wide.
  totalCell: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', width: '100%' },
  // The toggle + pill group itself, right-hugged within `totalCell`.
  totalControls: { display: 'flex', alignItems: 'center', gap: '10px' },
  // `wrap` is a flex COLUMN, so its default `align-items: stretch` would draw an
  // inline-flex pill as a full-width band. Right-align it under the Total column
  // the way the Line Items Summary's own chip sits under its total. Only the
  // awaiting-a-department pill uses this now — the Reconciles pill moved
  // inline into `totalCell` above (owner, 2026-08-14, reverting item 15,
  // 2026-08-13) — but it is kept as its own class rather than folded into
  // `wrap` because a second pill line may join it later and both would still
  // want the same rule.
  pillLine: { display: 'flex', justifyContent: 'flex-end', marginTop: '-8px' },
})

function codeLabel(r: GLSummaryRow): string {
  const name = r.codeName?.trim()
  if (name) return name
  const id = r.codeId?.trim()
  if (id) return id
  return NO_CODE
}

function accountLabel(r: GLSummaryRow): string {
  const label = r.accountLabel?.trim()
  if (label) return label
  return r.accountId?.trim() || NO_ACCOUNT
}

/** One grouped code or account row, as rendered. `key` names the group
    across the whole invoice, which is how the line-grain distribution is
    added back onto it. */
interface GroupedRow {
  key: string
  label: string
  total: number
}

/** The grouping key of one line: its account at the account grouping, else
    its code id, else its code name, else the "(No code)" bucket. */
function groupKeyOf(r: GLSummaryRow, groupBy: 'code' | 'account'): string {
  return groupBy === 'account'
    ? r.accountId?.trim() || NO_ACCOUNT
    : r.codeId?.trim() || r.codeName?.trim() || NO_CODE
}

/** { key, label, total } grouped by code, sorted descending by total. Keyed
    by code id, else name — a flow-written row can carry a name with no id,
    and must get its own correctly-labelled row (two id-less named codes stay
    distinct) rather than joining the "(No code)" bucket. Only rows with
    neither id nor name land there. */
function byCode(rows: GLSummaryRow[], groupBy: 'code' | 'account'): GroupedRow[] {
  const map = new Map<string, GroupedRow>()
  for (const r of rows) {
    const key = groupKeyOf(r, groupBy)
    const existing = map.get(key)
    if (existing) {
      existing.total += r.total
    } else {
      map.set(key, {
        key,
        label: groupBy === 'account' ? accountLabel(r) : codeLabel(r),
        total: r.total,
      })
    }
  }
  return [...map.values()].sort((a, b) => b.total - a.total)
}

export function GLSummary({ rows, groupBy = 'code', subtotal, tax, shipping, total }: Props) {
  const s = useStyles()
  const ls = useSummaryLabelStyles()
  // The toggle's own state. A VIEW preference only — never sent anywhere, never
  // read from anywhere — so it starts OFF (today's behaviour) on every mount
  // and carries no memory between invoices, same as any other display option
  // on this read-only rollup.
  const [distribute, setDistribute] = useState(false)
  if (!rows || rows.length === 0) return null

  // RAW pre-tax sum — the reconcile check below is ALWAYS against this figure,
  // never against the tax-distributed one: the toggle changes what is
  // DISPLAYED, never what is CHECKED. Flipping a view preference must not turn
  // a green pill red or vice versa.
  const grand = rows.reduce((sum, r) => sum + r.total, 0)
  // Green only. A red "Doesn't reconcile" here would restate, six inches lower,
  // the red pill the Line Items footer already shows. With no printed amounts
  // there is nothing to reconcile against and nothing renders.
  //
  // `shipping` JOINS the other three (owner item 4, 2026-08-18) — it used to
  // be left out here while `InvoiceLineItemsTable.tsx`'s own `amountsKnown`
  // included it, so an invoice with only Shipping known (no subtotal, tax or
  // total) showed this pill hidden here and the equivalent toggle enabled
  // there. This is the SAME constant that gates the "View With Tax & Shipping
  // Distributed" toggle below, so the fix changes both at once: an
  // invoice known only on shipping now also shows the Reconciles pill (or its
  // absence) here, consistently with the toggle.
  const amountsKnown = subtotal != null || tax != null || total != null || shipping != null
  // Lines plus tax plus shipping against the Total — one comparison (D-151).
  const reconciles = amountsKnown && checkAmounts(grand, tax, total, shipping).reconciles

  // Gated to the code grouping: Import Control (the only step that mounts
  // this component with `groupBy="account"`) holds the department as an
  // option only, so ungated this would claim nearly every line on a screen
  // where no department is owed yet.
  const awaiting = groupBy === 'code' ? rows.filter(isAwaitingDepartment) : []
  const awaitingTotal = awaiting.reduce((sum, r) => sum + r.total, 0)

  // "DISTRIBUTE TAX & SHIPPING" — gated only to KNOWN amounts now: with none
  // there is nothing to distribute against, which is the same condition that
  // hides the Reconciles pill above, so the toggle hides with it rather than
  // rendering a control that can never do anything. It is NO LONGER gated to the
  // code grouping (D-7) — see the header comment for what that costs at
  // Import Control and why the owner asked for it anyway.
  const canDistribute = amountsKnown
  const taxDollars = toNumber(tax)
  const shippingDollars = toNumber(shipping)
  // ALWAYS ENABLED once it renders at all (owner item 4, 2026-08-18) — it used
  // to disable when both charges were known-zero, on the theory that there was
  // nothing to spread. `allocateChargeDollars`
  // (`app/src/lib/taxDistribution.ts`) early-returns cleanly on a zero charge
  // or a zero subtotal — spreading a $0 charge is just zero shares, not a
  // division error — so a known-zero pair was never a real reason to lock the
  // switch; it only ever looked that way because null used to be
  // indistinguishable from zero here, and null is impossible now.
  const distributing = canDistribute && distribute

  // The groups, raw. When the toggle is on, each group's figure is replaced by
  // the sum of its lines' own CSV rows: the charges and the vendor's variance
  // are spread across the invoice's FULL pool of LINES — the pool
  // `exportBuilder.ts` spreads across — and only then added up by group
  // (`distributeByGroup`). Spreading over the groups themselves would put the
  // leftover cents on the largest group, not the largest line the CSV uses
  // (D-151).
  const flat: GroupedRow[] = byCode(rows, groupBy)
  const distributedByGroup = distributing
    ? distributeByGroup(
        rows.map((r) => ({ row: r, total: r.total, sequence: r.lineNumber ?? 0 })),
        (line) => groupKeyOf(line.row, groupBy),
        taxDollars,
        shippingDollars,
        total === undefined || total === null || total === '' ? null : toNumber(total),
      )
    : null
  const displayed: GroupedRow[] = distributedByGroup
    ? flat.map((g) => ({ ...g, total: distributedByGroup.get(g.key) ?? g.total }))
    : flat
  const displayGrand = displayed.reduce((sum, a) => sum + a.total, 0)

  return (
    <div className={s.wrap}>
      {/* The heading "GL Summary" and the rule above it are the modal's
          `CollapsibleSection` since D-329 (owner, 2026-10-08). */}
      <Table size="small" aria-label="GL summary" className={s.table}>
        <TableHeader>
          <TableRow>
            {/* "Code" — the owner's ruling of 2026-09-24 (`docs/decisions.md`
                D-139), with the section heading "GL Summary". Not the bare
                "Account" the retired per-line picker column used to carry
                (owner item 7, 2026-08-19: "Account should never appear
                anywhere in the Invoices tab"), and, since item 8 (2026-08-19,
                docs/decisions.md D-43) renamed "What Is It?" to the bare
                "Account" everywhere else, also not a label that would collide
                with THAT column's meaning (the account). This rollup groups by
                the WHOLE code (account + department together), and since
                D-139 "code" means exactly that and nothing else, so the bare
                word is unambiguous. It read "GL Account" and then "GL Code"
                before the ruling. `InvoiceLineItemsTable.tsx`'s own
                composed-code column (`showCode`, which no screen renders
                today) carries the identical label for the identical reason.
                HEADER ONLY — this rollup's GRAIN stays the composed code
                (`byCode` above still groups by `codeId`/`codeName`);
                changing the label is cosmetic, changing the grain is not
                (the distributed view adds the CSV's per-line rows up by
                code, `docs/export.md` §Sales tax). */}
            <TableHeaderCell>{groupBy === 'account' ? 'Account' : 'Code'}</TableHeaderCell>
            <TableHeaderCell style={{ width: 120 }} className={s.numHead}>Total</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayed.map((a) => (
            <TableRow key={a.key}>
              <TableCell className={a.label === NO_CODE || a.label === NO_ACCOUNT ? s.none : undefined}>{a.label}</TableCell>
              <TableCell className={s.num}>{formatCurrency(a.total)}</TableCell>
            </TableRow>
          ))}
          <TableRow className={s.grandRow}>
            {/* THE LABEL CELL — the "View With Tax & Shipping Distributed" toggle and the
                Reconciles pill, inline, right-hugging the amount cell next
                door. The far-left "Total" is deliberately gone (owner,
                2026-08-14); see `totalCell`'s comment above for how this
                preserves the amount COLUMN's own alignment: nothing here
                touches the amount cell below. */}
            <TableCell>
              <div className={s.totalCell}>
                <div className={s.totalControls}>
                  {canDistribute && (
                    <Switch
                      size="small"
                      labelPosition="before"
                      label="View With Tax &amp; Shipping Distributed"
                      checked={distribute}
                      onChange={(_e, d) => setDistribute(d.checked)}
                    />
                  )}
                  {reconciles && <DotPill pill={TINT_PILLS.green}>Reconciles</DotPill>}
                  <span className={ls.label}>{distributing ? 'Invoice Total:' : 'Invoice Subtotal:'}</span>
                </div>
              </div>
            </TableCell>
            {/* THE AMOUNT CELL — unchanged: same `width: 120`, same `s.num`
                (`text-align: right`), same bare `formatCurrency(...)` content
                as every grouped row's amount cell above it and as this exact
                cell before this change. Only the NUMBER shown moved (raw
                `grand` vs. the toggle's `displayGrand`, which sums the SAME
                `displayed` rows rendered above) — the cell's shape, and so its
                right-edge alignment with the column above it, did not. */}
            <TableCell className={s.num}>{formatCurrency(displayGrand)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {awaiting.length > 0 && (
        <div className={s.pillLine}>
          <DotPill pill={TINT_PILLS.yellow}>
            {/* "Department" — owner item 8's rename (2026-08-19,
                `docs/decisions.md` D-43) missed this pill, so the one screen
                that asks for the value called it "Department" in its
                subtitle, its column header and its two warnings while this
                line used the old internal name, which a reader had seen
                nowhere else (QA walk, 2026-08-30). `docs/glossary.md` is
                explicit: the leading half is what the DEPARTMENT column asks. */}
            {formatCurrency(awaitingTotal)} awaiting a Department
            {awaiting.length === 1 ? ' (1 line)' : ` (${awaiting.length} lines)`}
          </DotPill>
        </div>
      )}
    </div>
  )
}
